"use client";

import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import UserContext from '@/features/auth/utils/user-context-provider';
import { getRequisites, saveRequisites } from '@/features/consent/utils/consent-api';
import type { TConsentStatus, TPersonType, TRequisites } from '@/features/consent/types';

const PERSON_TYPES: { value: TPersonType, label: string }[] = [
  { value: 'individual', label: 'Физическое лицо' },
  { value: 'self_employed', label: 'Самозанятый' },
  { value: 'ip', label: 'Индивидуальный предприниматель' },
];

// Требование принимающей стороны, не наша придирка: Подпислон отвергает документ
// с точкой, цифрой или кавычкой в имени — «Иванов И.И.» не пройдёт, а
// «Римский-Корсаков» и «Jean Luc» пройдут. Без этой проверки человек узнаёт об
// отказе не на форме, а через два окна, когда исправить уже негде.
const NAME_FORMAT = /^[\p{L}\- ]+$/u;
const NAME_MESSAGE = 'Только буквы, пробел и дефис — без точек, цифр и кавычек';

const requiredString = (max: number) =>
  z.string().trim().min(1, { message: 'Обязательное поле' })
    .max(max, { message: `Не длиннее ${max} символов` });

const personName = (max: number) =>
  requiredString(max).regex(NAME_FORMAT, { message: NAME_MESSAGE });

const isAdult = (value: string) => {
  const birth = new Date(value);

  if (Number.isNaN(birth.getTime())) return false;

  const eighteenth = new Date(birth.getFullYear() + 18, birth.getMonth(), birth.getDate());

  return eighteenth <= new Date();
}

// Телефон нормализуется на сервере, здесь достаточно отсечь заведомо не номер.
const isRussianPhone = (value: string) => {
  const digits = value.replace(/\D/g, '');

  if (digits.length === 10) return true;
  if (digits.length === 11) return digits.startsWith('7') || digits.startsWith('8');

  return false;
}

const formSchema = z.object({
  last_name: personName(255),
  first_name: personName(255),
  middle_name: z.string().trim().max(255, { message: 'Не длиннее 255 символов' })
    .refine((value) => value === '' || NAME_FORMAT.test(value), { message: NAME_MESSAGE }),
  country: requiredString(255),
  city: requiredString(255),
  person_type: z.enum(['individual', 'self_employed', 'ip']),
  birth_date: z.string().min(1, { message: 'Укажите дату рождения' })
    .refine(isAdult, { message: 'Только с 18 лет' }),
  birth_date_document: z.string(),
  passport_series: z.string().trim().regex(/^\d{4}$/, { message: 'Ровно 4 цифры' }),
  passport_number: z.string().trim().regex(/^\d{6}$/, { message: 'Ровно 6 цифр' }),
  passport_issued_at: z.string().min(1, { message: 'Укажите дату выдачи' }),
  passport_department_code: z.string().trim().regex(/^\d{3}-\d{3}$/, { message: 'Формат 123-456' }),
  passport_issued_by: requiredString(500),
  inn: z.string().trim().refine((value) => value === '' || /^\d{12}$/.test(value), {
    message: 'ИНН — ровно 12 цифр',
  }),
  registration_address: requiredString(1000),
  email: z.string().trim().email({ message: 'Некорректный адрес почты' }),
  phone: z.string().trim().refine(isRussianPhone, { message: 'Некорректный номер телефона' }),
}).superRefine((values, ctx) => {
  if (!values.passport_issued_at) return;

  const issuedAt = new Date(values.passport_issued_at);

  if (issuedAt > new Date()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['passport_issued_at'],
      message: 'Не может быть в будущем',
    });
  }

  if (values.birth_date && issuedAt < new Date(values.birth_date)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['passport_issued_at'],
      message: 'Раньше даты рождения',
    });
  }
});

type TFormValues = z.infer<typeof formSchema>;

const EMPTY_VALUES: TFormValues = {
  last_name: '',
  first_name: '',
  middle_name: '',
  country: 'Россия',
  city: '',
  person_type: 'individual',
  birth_date: '',
  birth_date_document: '',
  passport_series: '',
  passport_number: '',
  passport_issued_at: '',
  passport_department_code: '',
  passport_issued_by: '',
  inn: '',
  registration_address: '',
  email: '',
  phone: '',
};

// <input type="date"> понимает только `YYYY-MM-DD`: если бэкенд отдаст дату целиком
// («1990-04-12T00:00:00.000000Z»), поле молча останется пустым.
const DATE_FIELDS: (keyof TFormValues)[] = ['birth_date', 'birth_date_document', 'passport_issued_at'];

const toDateInputValue = (value: string) => /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : value;

// Паспортные поля приходят маскированными (`••10`). Правило одно на все поля:
// маскированное значение в форму не подставляем, а показываем подсказкой — так
// форма не сломается, если бэкенд начнёт прятать что-то ещё.
const isMasked = (value: unknown): value is string =>
  typeof value === 'string' && value.includes('•');

type Props = {
  onSaved: (status: TConsentStatus) => void;
}

// Окно 2 «Заполните реквизиты».
export default function ConsentRequisitesForm({ onSaved }: Props)
{
  const user = useContext(UserContext);

  const [isLoading, setIsLoading] = useState(true);
  const [maskedHints, setMaskedHints] = useState<Partial<Record<keyof TFormValues, string>>>({});

  const form = useForm<TFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: EMPTY_VALUES,
  });

  useEffect(() => {
    let cancelled = false;

    const loadRequisites = async () => {
      // Профиль — только запасной вариант. Сохранённые реквизиты вводили осознанно
      // и под документ, поэтому они перекрывают эти значения ниже.
      const values: TFormValues = {
        ...EMPTY_VALUES,
        last_name: user?.last_name ?? '',
        first_name: user?.first_name ?? '',
        email: user?.email ?? '',
      };
      const hints: Partial<Record<keyof TFormValues, string>> = {};

      try {
        const requisites = await getRequisites();

        if (cancelled) return;

        if (requisites) {
          (Object.keys(EMPTY_VALUES) as (keyof TFormValues)[]).forEach((field) => {
            const value = requisites[field as keyof TRequisites];

            if (isMasked(value)) {
              hints[field] = value;
              return;
            }

            if (value !== null && value !== undefined && value !== '') {
              const asString = String(value);

              // @ts-expect-error — поля формы строковые, значения из API совпадают по имени
              values[field] = DATE_FIELDS.includes(field) ? toDateInputValue(asString) : asString;
            }
          });
        }
      } catch {
        // Реквизитов может не быть вовсе — это не ошибка, останутся данные профиля.
      }

      if (cancelled) return;

      form.reset(values);
      setMaskedHints(hints);
      setIsLoading(false);
    };

    loadRequisites();

    return () => {
      cancelled = true;
    };
  }, [form, user]);

  const onSubmit = async (values: TFormValues) => {
    try {
      const { consent } = await saveRequisites(values as TRequisites);

      onSaved(consent.status);
    } catch (error: any) {
      const errors = error?.response?.data?.errors;

      if (error?.response?.status === 422 && errors) {
        Object.entries(errors).forEach(([field, messages]) => {
          form.setError(field as keyof TFormValues, {
            message: Array.isArray(messages) ? String(messages[0]) : String(messages),
          });
        });

        toast.error('Проверьте заполнение формы');
        return;
      }

      toast.error('Не удалось сохранить реквизиты');
    }
  }

  const maskedHint = (field: keyof TFormValues) => maskedHints[field]
    ? <FormDescription>Ранее заполнено: {maskedHints[field]}. Введите значение заново.</FormDescription>
    : null;

  if (isLoading) {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Заполните реквизиты</DialogTitle>
          <DialogDescription>Загружаем сохранённые данные…</DialogDescription>
        </DialogHeader>

        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </>
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Заполните реквизиты</DialogTitle>
        <DialogDescription>
          По этим данным формируется текст согласия. Паспортные данные и ИНН нужны
          только для документа и в фотобанке не публикуются.
        </DialogDescription>
      </DialogHeader>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">

          <div className="space-y-4">
            <h3 className="text-sm font-medium">Личные данные</h3>

            <div className="grid items-start gap-4 sm:grid-cols-3">
              <FormField
                control={form.control}
                name="last_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Фамилия</FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete="family-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Имя</FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete="given-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="middle_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Отчество</FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete="additional-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <p className="text-sm text-muted-foreground">
              Проверьте, что ФИО совпадает с паспортом: оно попадёт в документ и в
              сервис подписания.
            </p>

            <div className="grid items-start gap-4 sm:grid-cols-3">
              <FormField
                control={form.control}
                name="birth_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Дата рождения</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Страна</FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete="country-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Город</FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete="address-level2" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="person_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Тип лица</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {PERSON_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>Влияет только на текст документа.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-medium">Паспорт</h3>

            <div className="grid items-start gap-4 sm:grid-cols-4">
              <FormField
                control={form.control}
                name="passport_series"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Серия</FormLabel>
                    <FormControl>
                      <Input inputMode="numeric" maxLength={4} placeholder="4510" {...field} />
                    </FormControl>
                    {maskedHint('passport_series')}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="passport_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Номер</FormLabel>
                    <FormControl>
                      <Input inputMode="numeric" maxLength={6} placeholder="987654" {...field} />
                    </FormControl>
                    {maskedHint('passport_number')}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="passport_issued_at"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Дата выдачи</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="passport_department_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Код подразделения</FormLabel>
                    <FormControl>
                      <Input maxLength={7} placeholder="770-053" {...field} />
                    </FormControl>
                    {maskedHint('passport_department_code')}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="passport_issued_by"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Кем выдан</FormLabel>
                  <FormControl>
                    <Textarea className="max-h-24" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="registration_address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Адрес регистрации</FormLabel>
                  <FormControl>
                    <Textarea className="max-h-24" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="inn"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ИНН</FormLabel>
                  <FormControl>
                    <Input inputMode="numeric" maxLength={12} {...field} />
                  </FormControl>
                  {maskedHint('inn') ?? <FormDescription>Необязательно.</FormDescription>}
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-medium">Контакты</h3>

            <div className="grid items-start gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Электронная почта</FormLabel>
                    <FormControl>
                      <Input type="email" autoComplete="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Телефон</FormLabel>
                    <FormControl>
                      <Input type="tel" autoComplete="tel" placeholder="+7 (999) 123-45-67" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button type="submit" disabled={form.formState.isSubmitting} className="w-full sm:w-auto">
              {form.formState.isSubmitting ? (
                <>
                  Сохраняем
                  <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                </>
              ) : 'Сохранить и перейти к подписанию'}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}
